import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  FaChartLine, FaClipboardList, FaTrophy, FaFileAlt,
  FaPlus, FaTrash, FaArrowUp, FaArrowDown,
  FaMinus, FaChevronDown, FaChevronUp, FaFilter,
  FaFire, FaStar, FaSpinner, FaCheckCircle, FaLightbulb,
  FaExclamationTriangle, FaCalendarAlt, FaListOl, FaRedo,
  FaTags,
} from 'react-icons/fa';
import {
  submitWeeklyReport, getCurrentWeekReport, getMyReports,
  getAllReports, getLeaderboard, getReportWeeks,
} from '../services/performanceApi';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement,
  LineController, Title, Tooltip, Legend, Filler,
} from 'chart.js';
import './WeeklyPerformance.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, LineController, Title, Tooltip, Legend, Filler);

// ── Helpers ──────────────────────────────────────────────────────────────────
function formatWeek(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  const end = new Date(d);
  end.setDate(end.getDate() + 6);
  const opts = { timeZone: 'Asia/Kolkata', month: 'short', day: 'numeric' };
  return `${d.toLocaleDateString('en-IN', opts)} – ${end.toLocaleDateString('en-IN', opts)}, ${d.getFullYear()}`;
}

function getWeekStartDate() {
  const now = new Date();
  const ist = now.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  const d = new Date(ist + 'T00:00:00');
  const day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getPrevWeekLabel(weekStart) {
  if (!weekStart) return '';
  const d = new Date(weekStart + 'T00:00:00');
  d.setDate(d.getDate() - 7);
  return formatWeek(d.toISOString().split('T')[0]);
}

function getNextWeekLabel(weekStart) {
  if (!weekStart) return '';
  const d = new Date(weekStart + 'T00:00:00');
  d.setDate(d.getDate() + 7);
  return formatWeek(d.toISOString().split('T')[0]);
}

// ── Task Categories ───────────────────────────────────────────────────────────
const TASK_CATEGORIES = {
  dpps:     { label: 'DPPs',           metrics: [{ key: 'num_questions', label: 'Number of Questions', placeholder: 'e.g. 25' }] },
  tests:    { label: 'Tests',          metrics: [{ key: 'num_questions', label: 'Number of Questions', placeholder: 'e.g. 30' }] },
  lectures: { label: 'Lecture Videos', metrics: [{ key: 'num_videos', label: 'Number of Videos', placeholder: 'e.g. 5' }, { key: 'total_minutes', label: 'Total Minutes', placeholder: 'e.g. 120' }] },
  content:  { label: 'Content / Notes',metrics: [{ key: 'num_pages', label: 'Pages / Topics', placeholder: 'e.g. 15' }] },
  doubts:   { label: 'Doubt Solving',  metrics: [{ key: 'num_doubts', label: 'Doubts Resolved', placeholder: 'e.g. 10' }] },
  meetings: { label: 'Meetings',       metrics: [{ key: 'num_meetings', label: 'Meetings Count', placeholder: 'e.g. 3' }, { key: 'total_minutes', label: 'Total Minutes', placeholder: 'e.g. 90' }] },
  other:    { label: 'Other',          metrics: [] },
};

function humanizeMetricKey(key) {
  return key.replace(/^num_/, '').replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}

// ═════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
const WeeklyPerformance = () => {
  const { userData } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'dashboard');

  const handleTabChange = (tab) => { setActiveTab(tab); setSearchParams({ tab }); };

  const tabs = [
    { id: 'dashboard',   label: 'Dashboard',     icon: <FaChartLine /> },
    { id: 'report',      label: 'Submit Report',  icon: <FaClipboardList /> },
    { id: 'leaderboard', label: 'Leaderboard',    icon: <FaTrophy /> },
    { id: 'reports',     label: 'All Reports',    icon: <FaFileAlt /> },
  ];

  return (
    <div className="wp-page">
      <div className="wp-page-head">
        <div className="wp-page-head-left">
          <h1 className="wp-page-title">Weekly Progress</h1>
          <p className="wp-page-desc">Track, submit, and compare your weekly performance reports</p>
        </div>
        <div className="wp-week-tag">
          <FaCalendarAlt />
          <span>{formatWeek(getWeekStartDate())}</span>
        </div>
      </div>

      <nav className="wp-nav">
        {tabs.map((tab) => (
          <button key={tab.id} className={`wp-nav-btn${activeTab === tab.id ? ' active' : ''}`} onClick={() => handleTabChange(tab.id)}>
            <span className="wp-nav-icon">{tab.icon}</span>
            <span className="wp-nav-label">{tab.label}</span>
          </button>
        ))}
      </nav>
      <select className="wp-nav-select" value={activeTab} onChange={(e) => handleTabChange(e.target.value)}>
        {tabs.map((tab) => <option key={tab.id} value={tab.id}>{tab.label}</option>)}
      </select>

      <div className="wp-page-body">
        {activeTab === 'dashboard'   && <DashboardTab />}
        {activeTab === 'report'      && <ReportFormTab />}
        {activeTab === 'leaderboard' && <LeaderboardTab />}
        {activeTab === 'reports'     && <AllReportsTab userData={userData} />}
      </div>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// TAB 1 – DASHBOARD
// ═════════════════════════════════════════════════════════════════════════════
const DashboardTab = () => {
  const [reports, setReports]       = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading]       = useState(true);
  const { userData }                = useAuth();

  useEffect(() => {
    (async () => {
      try {
        const [myData, lbData] = await Promise.all([getMyReports(), getLeaderboard()]);
        setReports(myData.reports || []);
        setLeaderboard(lbData.leaderboard || []);
      } catch (err) { console.error(err); toast.error('Failed to load dashboard data'); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <LoadingSpinner />;

  const latestReport = reports[0];
  const last4        = reports.slice(0, 4);
  const myRank       = leaderboard.find((r) => r.employee_id === userData?.uid);

  // Streak
  let streak = 0, checkDate = getWeekStartDate();
  for (const week of reports.map((r) => r.week_start_date).sort().reverse()) {
    if (week === checkDate) {
      streak++;
      const d = new Date(checkDate + 'T00:00:00'); d.setDate(d.getDate() - 7);
      checkDate = d.toISOString().split('T')[0];
    } else if (week < checkDate) break;
  }

  const improvement = reports.length >= 2
    ? (reports[0].performance_score - reports[1].performance_score).toFixed(1) : null;

  const chartReports = [...last4].reverse();
  const chartData = {
    labels: chartReports.map((r) => formatWeek(r.week_start_date)),
    datasets: [
      { label: 'Performance', data: chartReports.map((r) => r.performance_score), borderColor: '#6366f1', backgroundColor: 'rgba(99,102,241,0.08)', fill: true, tension: 0.4, pointRadius: 4, pointBackgroundColor: '#6366f1', borderWidth: 2 },
      { label: 'Completion',  data: chartReports.map((r) => r.completion_score),  borderColor: '#10b981', backgroundColor: 'rgba(16,185,129,0.06)', fill: true, tension: 0.4, pointRadius: 4, pointBackgroundColor: '#10b981', borderWidth: 2 },
    ],
  };
  const chartOptions = {
    responsive: true, maintainAspectRatio: false,
    scales: {
      y: { min: 0, max: 100, ticks: { color: 'var(--color-text-alt)', font: { size: 11 } }, grid: { color: 'var(--color-border)' } },
      x: { ticks: { color: 'var(--color-text-alt)', maxRotation: 30, font: { size: 11 } }, grid: { display: false } },
    },
    plugins: {
      legend: { display: false },
      tooltip: { backgroundColor: 'var(--color-background-card)', titleColor: 'var(--color-text)', bodyColor: 'var(--color-text-alt)', borderColor: 'var(--color-border)', borderWidth: 1 },
    },
  };

  return (
    <div className="wp-dash">
      {/* ── Hero Banner ── */}
      <div className="wp-hero">
        <div className="wp-hero-left">
          <span className="wp-hero-eyebrow">Your Performance Score</span>
          <div className="wp-hero-score-row">
            <span className="wp-hero-num">{latestReport ? latestReport.performance_score : '—'}</span>
            <span className="wp-hero-denom">/ 100</span>
          </div>
          {latestReport && <span className="wp-hero-week-label">{formatWeek(latestReport.week_start_date)}</span>}
        </div>
        <div className="wp-hero-divider" />
        <div className="wp-hero-kpis">
          <div className="wp-hero-kpi">
            <FaTrophy className="wp-hkpi-icon gold" />
            <span className="wp-hkpi-val">{myRank ? `#${myRank.rank}` : '—'}</span>
            <span className="wp-hkpi-lbl">Team Rank</span>
          </div>
          <div className="wp-hero-kpi">
            <FaFire className="wp-hkpi-icon orange" />
            <span className="wp-hkpi-val">{streak}</span>
            <span className="wp-hkpi-lbl">Week Streak</span>
          </div>
          <div className="wp-hero-kpi">
            {improvement > 0 ? <FaArrowUp className="wp-hkpi-icon green" />
              : improvement < 0 ? <FaArrowDown className="wp-hkpi-icon red" />
              : <FaMinus className="wp-hkpi-icon muted" />}
            <span className="wp-hkpi-val">{improvement !== null ? `${improvement > 0 ? '+' : ''}${improvement}` : '—'}</span>
            <span className="wp-hkpi-lbl">vs Last Week</span>
          </div>
        </div>
      </div>

      {/* ── Chart + Snapshot ── */}
      {chartReports.length > 0 ? (
        <div className="wp-dash-grid">
          <div className="wp-card wp-chart-card">
            <div className="wp-card-head">
              <h3 className="wp-card-title">Performance Trend</h3>
              <div className="wp-chart-legend">
                <div className="wp-legend-item"><span className="wp-legend-dot" style={{ background: '#6366f1' }} /><span>Score</span></div>
                <div className="wp-legend-item"><span className="wp-legend-dot" style={{ background: '#10b981' }} /><span>Completion</span></div>
              </div>
            </div>
            <div className="wp-chart-body">
              <canvas ref={(el) => {
                if (!el) return;
                const ex = ChartJS.getChart(el); if (ex) ex.destroy();
                new ChartJS(el, { type: 'line', data: chartData, options: chartOptions });
              }} />
            </div>
          </div>

          {last4[0] && (
            <div className="wp-card wp-snapshot-card">
              <div className="wp-card-head">
                <h3 className="wp-card-title">Latest Week</h3>
                <span className="wp-chip">{formatWeek(last4[0].week_start_date)}</span>
              </div>
              <div className="wp-snapshot-body">
                <div className="wp-smetric">
                  <div className="wp-smetric-top">
                    <span className="wp-smetric-val">{last4[0].performance_score}</span>
                    {improvement !== null && (
                      <span className={`wp-smetric-trend ${Number(improvement) > 0 ? 'up' : Number(improvement) < 0 ? 'down' : ''}`}>
                        {Number(improvement) > 0 ? `+${improvement}` : improvement}
                      </span>
                    )}
                  </div>
                  <span className="wp-smetric-lbl">Performance Score</span>
                  <div className="wp-sbar"><div className="wp-sbar-fill indigo" style={{ width: `${last4[0].performance_score}%` }} /></div>
                </div>
                <div className="wp-smetric">
                  <div className="wp-smetric-top">
                    <span className="wp-smetric-val">{last4[0].completion_score}%</span>
                  </div>
                  <span className="wp-smetric-lbl">Task Completion</span>
                  <div className="wp-sbar">
                    <div className={`wp-sbar-fill ${last4[0].completion_score >= 80 ? 'emerald' : last4[0].completion_score >= 50 ? 'amber' : 'red'}`} style={{ width: `${last4[0].completion_score}%` }} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="wp-empty-panel">
          <div className="wp-empty-ico"><FaClipboardList /></div>
          <div className="wp-empty-body">
            <strong>No reports submitted yet</strong>
            <p>Submit your first weekly report to see performance insights and trends.</p>
          </div>
        </div>
      )}

      {/* ── Report History ── */}
      {last4.length > 0 && (
        <div className="wp-card">
          <div className="wp-card-head">
            <h3 className="wp-card-title">Report History</h3>
            <span className="wp-chip">{reports.length} {reports.length === 1 ? 'report' : 'reports'}</span>
          </div>
          <div className="wp-history">
            {last4.map((r, i) => (
              <div key={r.id} className="wp-history-row">
                <div className="wp-history-num">{i + 1}</div>
                <div className="wp-history-week">{formatWeek(r.week_start_date)}</div>
                <div className="wp-history-bar-wrap"><div className="wp-history-bar" style={{ width: `${r.performance_score}%` }} /></div>
                <div className="wp-history-chips">
                  <span className="wp-chip wp-chip-indigo">{r.performance_score} pts</span>
                  <span className="wp-chip wp-chip-emerald">{r.completion_score}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// TAB 2 – SUBMIT REPORT
// ═════════════════════════════════════════════════════════════════════════════
const EMPTY_TASK = { description: '', category: '', status: '', metrics: {}, customMetrics: [{ key: '', value: '' }] };
const EMPTY_PLAN = { description: '', category: '', metrics: {}, customMetrics: [{ key: '', value: '' }] };

const ReportFormTab = () => {
  const [loading, setLoading]           = useState(true);
  const [submitting, setSubmitting]     = useState(false);
  const [editWindowOpen, setEditWindowOpen] = useState(true);
  const [existingReport, setExistingReport] = useState(null);
  const [weekStart, setWeekStart]       = useState('');
  const [prevWeekPlan, setPrevWeekPlan] = useState([]);
  const [tasks, setTasks]               = useState([{ ...EMPTY_TASK, customMetrics: [{ key: '', value: '' }] }]);
  const [wins, setWins]                 = useState('');
  const [failures, setFailures]         = useState('');
  const [nextWeekPlan, setNextWeekPlan] = useState([{ ...EMPTY_PLAN }]);
  const [blockers, setBlockers]         = useState('');

  useEffect(() => {
    (async () => {
      try {
        const data = await getCurrentWeekReport();
        setWeekStart(data.weekStart);
        setEditWindowOpen(data.editWindowOpen);
        const prevPlan = data.previousWeekPlan || [];
        setPrevWeekPlan(prevPlan);
        if (data.report) {
          setExistingReport(data.report);
          const r = data.report;
          if (r.tasks?.length) {
            setTasks(r.tasks.map((t) => ({ description: t.description || '', category: t.category || '', status: t.status || '', metrics: t.metrics || {}, customMetrics: t.category === 'other' && t.metrics ? Object.entries(t.metrics).map(([k, v]) => ({ key: k, value: String(v) })) : [{ key: '', value: '' }] })));
          } else if (r.last_week_commitments?.length) {
            setTasks(r.last_week_commitments.map((c) => ({ ...EMPTY_TASK, description: c, customMetrics: [{ key: '', value: '' }] })));
          }
          setWins(r.wins || ''); setFailures(r.failures || '');
          if (r.next_week_plan?.length) {
            if (typeof r.next_week_plan[0] === 'string') { setNextWeekPlan(r.next_week_plan.map((p) => ({ description: p, category: '', metrics: {}, customMetrics: [{ key: '', value: '' }] }))); }
            else { setNextWeekPlan(r.next_week_plan.map((p) => ({ description: p.description || '', category: p.category || '', metrics: p.metrics || {}, customMetrics: p.category === 'other' && p.metrics ? Object.entries(p.metrics).map(([k, v]) => ({ key: k, value: String(v) })) : [{ key: '', value: '' }] }))); }
          }
          setBlockers(r.blockers || '');
        } else if (prevPlan.length > 0) {
          if (typeof prevPlan[0] === 'string') { setTasks(prevPlan.map((p) => ({ ...EMPTY_TASK, description: p, customMetrics: [{ key: '', value: '' }] }))); }
          else { setTasks(prevPlan.map((p) => ({ description: p.description || '', category: p.category || '', status: '', metrics: p.metrics || {}, customMetrics: p.category === 'other' && p.metrics ? Object.entries(p.metrics).map(([k, v]) => ({ key: k, value: String(v) })) : [{ key: '', value: '' }] }))); }
        }
      } catch (err) { console.error(err); toast.error('Failed to load report'); }
      finally { setLoading(false); }
    })();
  }, []);

  const addTask   = () => setTasks((p) => [...p, { ...EMPTY_TASK, customMetrics: [{ key: '', value: '' }] }]);
  const removeTask = (i) => setTasks((p) => (p.length > 1 ? p.filter((_, j) => j !== i) : p));
  const updateTask = (i, field, value) => setTasks((p) => p.map((t, j) => j !== i ? t : field === 'category' ? { ...t, category: value, metrics: {}, customMetrics: [{ key: '', value: '' }] } : { ...t, [field]: value }));
  const updateTaskMetric = (ti, mk, v) => setTasks((p) => p.map((t, j) => j !== ti ? t : { ...t, metrics: { ...t.metrics, [mk]: v } }));
  const addCustomMetric  = (ti) => setTasks((p) => p.map((t, j) => j !== ti ? t : { ...t, customMetrics: [...t.customMetrics, { key: '', value: '' }] }));
  const updateCustomMetric = (ti, mi, field, v) => setTasks((p) => p.map((t, j) => j !== ti ? t : { ...t, customMetrics: t.customMetrics.map((m, k) => k !== mi ? m : { ...m, [field]: v }) }));
  const removeCustomMetric = (ti, mi) => setTasks((p) => p.map((t, j) => j !== ti ? t : { ...t, customMetrics: t.customMetrics.length > 1 ? t.customMetrics.filter((_, k) => k !== mi) : t.customMetrics }));

  const addPlanItem    = () => setNextWeekPlan((p) => [...p, { ...EMPTY_PLAN }]);
  const removePlanItem = (i) => setNextWeekPlan((p) => (p.length > 1 ? p.filter((_, j) => j !== i) : p));
  const updatePlanItem = (i, field, value) => setNextWeekPlan((p) => p.map((t, j) => j !== i ? t : field === 'category' ? { ...t, category: value, metrics: {}, customMetrics: [{ key: '', value: '' }] } : { ...t, [field]: value }));
  const updatePlanMetric = (i, mk, v) => setNextWeekPlan((p) => p.map((t, j) => j !== i ? t : { ...t, metrics: { ...t.metrics, [mk]: v } }));
  const addPlanCustomMetric = (i) => setNextWeekPlan((p) => p.map((t, j) => j !== i ? t : { ...t, customMetrics: [...t.customMetrics, { key: '', value: '' }] }));
  const updatePlanCustomMetric = (i, mi, field, v) => setNextWeekPlan((p) => p.map((t, j) => j !== i ? t : { ...t, customMetrics: t.customMetrics.map((m, k) => k !== mi ? m : { ...m, [field]: v }) }));
  const removePlanCustomMetric = (i, mi) => setNextWeekPlan((p) => p.map((t, j) => j !== i ? t : { ...t, customMetrics: t.customMetrics.length > 1 ? t.customMetrics.filter((_, k) => k !== mi) : t.customMetrics }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const filtered = tasks.filter((t) => t.description.trim());
    if (!filtered.length) { toast.error('Add at least one task with a description'); return; }
    if (filtered.find((t) => !t.status)) { toast.error('Please mark status for every task'); return; }
    const submittableTasks = filtered.map((t) => {
      const m = {};
      if (t.category && TASK_CATEGORIES[t.category]) for (const mk of TASK_CATEGORIES[t.category].metrics) { const v = Number(t.metrics[mk.key]); if (!isNaN(v) && v > 0) m[mk.key] = v; }
      if (t.category === 'other' && t.customMetrics) for (const cm of t.customMetrics) { if (cm.key.trim() && cm.value) m[cm.key.trim()] = Number(cm.value) || 0; }
      return { description: t.description.trim(), category: t.category, status: t.status, metrics: m };
    });
    const filteredPlan = nextWeekPlan.filter((p) => p.description.trim()).map((p) => {
      const m = {};
      if (p.category && TASK_CATEGORIES[p.category]) for (const mk of TASK_CATEGORIES[p.category].metrics) { const v = Number(p.metrics?.[mk.key]); if (!isNaN(v) && v > 0) m[mk.key] = v; }
      if (p.category === 'other' && p.customMetrics) for (const cm of p.customMetrics) { if (cm.key.trim() && cm.value) m[cm.key.trim()] = Number(cm.value) || 0; }
      return { description: p.description.trim(), category: p.category, metrics: m };
    });
    setSubmitting(true);
    try {
      await submitWeeklyReport({ tasks: submittableTasks, wins: wins.trim(), failures: failures.trim(), next_week_plan: filteredPlan, blockers: blockers.trim() });
      toast.success(existingReport ? 'Report updated!' : 'Report submitted!');
      setExistingReport(true);
    } catch (err) { console.error(err); toast.error(err?.message || 'Failed to submit report'); }
    finally { setSubmitting(false); }
  };

  if (loading) return <LoadingSpinner />;

  if (!editWindowOpen) return (
    <div className="wp-empty-panel">
      <div className="wp-empty-ico"><FaClipboardList /></div>
      <div className="wp-empty-body"><strong>Edit window closed</strong><p>Reports can only be edited within the same week.</p></div>
    </div>
  );

  return (
    <div className="wp-form-wrap">
      {existingReport && (
        <div className="wp-notice success">
          <FaCheckCircle className="wp-notice-icon" />
          <div>
            <strong>Report already submitted</strong>
            <span>You can update it until the edit window closes this week.</span>
          </div>
        </div>
      )}

      <div className="wp-form-topbar">
        <h2 className="wp-form-title">{existingReport ? 'Update Weekly Report' : 'Submit Weekly Report'}</h2>
        <p className="wp-form-period"><FaCalendarAlt /> Reporting for: <strong>{formatWeek(weekStart)}</strong></p>
      </div>

      <form onSubmit={handleSubmit} className="wp-form">

        {/* Step 1 – Tasks */}
        <div className="wp-step">
          <div className="wp-step-head">
            <span className="wp-step-num">1</span>
            <div>
              <h3 className="wp-step-title">This Week&apos;s Work</h3>
              <p className="wp-step-desc">Add each task with category and metrics — used for scoring</p>
            </div>
          </div>

          {prevWeekPlan.length > 0 && !existingReport && (
            <div className="wp-autofill-bar">
              <FaLightbulb />
              <span>Auto-filled from your plan for <strong>{getPrevWeekLabel(weekStart)}</strong>. Update as needed.</span>
            </div>
          )}

          <div className="wp-tasks">
            {tasks.map((task, i) => (
              <div key={i} className="wp-task">
                <div className="wp-task-head">
                  <div className="wp-task-head-left">
                    <span className="wp-task-idx">{i + 1}</span>
                    <span className="wp-task-lbl">Task {i + 1}</span>
                    {task.category && <span className="wp-tag cat">{TASK_CATEGORIES[task.category]?.label}</span>}
                    {task.status && <span className={`wp-tag status-${task.status}`}>{task.status === 'done' ? '✓ Done' : task.status === 'partially-done' ? '◐ Partial' : '✗ Not Done'}</span>}
                  </div>
                  {tasks.length > 1 && <button type="button" className="wp-icon-btn danger" onClick={() => removeTask(i)}><FaTrash /></button>}
                </div>
                <div className="wp-task-body">
                  <div className="wp-frow">
                    <div className="wp-field">
                      <label className="wp-flbl"><FaTags /> Category</label>
                      <select value={task.category} onChange={(e) => updateTask(i, 'category', e.target.value)} className="wp-input wp-select">
                        <option value="">Select category...</option>
                        {Object.entries(TASK_CATEGORIES).map(([k, c]) => <option key={k} value={k}>{c.label}</option>)}
                      </select>
                    </div>
                    <div className="wp-field wp-field-grow">
                      <label className="wp-flbl">Description</label>
                      <input type="text" value={task.description} onChange={(e) => updateTask(i, 'description', e.target.value)} placeholder="What did you work on?" className="wp-input" />
                    </div>
                  </div>

                  {task.category && TASK_CATEGORIES[task.category]?.metrics.length > 0 && (
                    <div className="wp-metrics-row">
                      {TASK_CATEGORIES[task.category].metrics.map((mk) => (
                        <div key={mk.key} className="wp-field">
                          <label className="wp-flbl">{mk.label}</label>
                          <input type="number" min="0" value={task.metrics[mk.key] || ''} onChange={(e) => updateTaskMetric(i, mk.key, e.target.value)} placeholder={mk.placeholder} className="wp-input wp-input-num" />
                        </div>
                      ))}
                    </div>
                  )}

                  {task.category === 'other' && (
                    <div className="wp-custom-metrics">
                      {task.customMetrics.map((m, j) => (
                        <div key={j} className="wp-cm-row">
                          <input type="text" value={m.key} onChange={(e) => updateCustomMetric(i, j, 'key', e.target.value)} placeholder="Metric name" className="wp-input" />
                          <input type="number" min="0" value={m.value} onChange={(e) => updateCustomMetric(i, j, 'value', e.target.value)} placeholder="Value" className="wp-input wp-input-sm" />
                          {task.customMetrics.length > 1 && <button type="button" className="wp-icon-btn danger sm" onClick={() => removeCustomMetric(i, j)}><FaTrash /></button>}
                        </div>
                      ))}
                      <button type="button" className="wp-ghost-btn sm" onClick={() => addCustomMetric(i)}><FaPlus /> Add Metric</button>
                    </div>
                  )}

                  <div className="wp-status-row">
                    <span className="wp-flbl">Status</span>
                    <div className="wp-status-pills">
                      {[{ val: 'done', label: '✓ Done', cls: 'done' }, { val: 'partially-done', label: '◐ Partial', cls: 'partial' }, { val: 'not-done', label: '✗ Not Done', cls: 'notdone' }].map(({ val, label, cls }) => (
                        <button key={val} type="button" className={`wp-status-pill ${cls}${task.status === val ? ' active' : ''}`} onClick={() => updateTask(i, 'status', val)}>{label}</button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <button type="button" className="wp-ghost-btn" onClick={addTask}><FaPlus /> Add Task</button>
        </div>

        {/* Step 2 – Highlights */}
        <div className="wp-step">
          <div className="wp-step-head">
            <span className="wp-step-num" style={{ background: '#10b981' }}>2</span>
            <div>
              <h3 className="wp-step-title">Highlights &amp; Learnings</h3>
              <p className="wp-step-desc">Reflect on what went well and what you learnt this week</p>
            </div>
          </div>
          <div className="wp-two-col">
            <div className="wp-field">
              <label className="wp-flbl"><FaStar style={{ color: '#d97706' }} /> Wins</label>
              <textarea value={wins} onChange={(e) => setWins(e.target.value)} placeholder="Key achievements, milestones hit..." className="wp-textarea" rows={4} />
            </div>
            <div className="wp-field">
              <label className="wp-flbl"><FaLightbulb style={{ color: '#d97706' }} /> Failures / Learnings</label>
              <textarea value={failures} onChange={(e) => setFailures(e.target.value)} placeholder="What didn't go as planned? What did you learn?" className="wp-textarea" rows={4} />
            </div>
          </div>
        </div>

        {/* Step 3 – Next Week Plan */}
        <div className="wp-step">
          <div className="wp-step-head">
            <span className="wp-step-num" style={{ background: '#f59e0b' }}>3</span>
            <div>
              <h3 className="wp-step-title">Next Week&apos;s Plan</h3>
              <p className="wp-step-desc">{getNextWeekLabel(weekStart)} — These will auto-fill your next report</p>
            </div>
          </div>

          <div className="wp-tasks">
            {nextWeekPlan.map((item, i) => (
              <div key={i} className="wp-task wp-task-plan">
                <div className="wp-task-head">
                  <div className="wp-task-head-left">
                    <span className="wp-task-idx plan">{i + 1}</span>
                    <span className="wp-task-lbl">Plan {i + 1}</span>
                    {item.category && <span className="wp-tag cat">{TASK_CATEGORIES[item.category]?.label}</span>}
                  </div>
                  {nextWeekPlan.length > 1 && <button type="button" className="wp-icon-btn danger" onClick={() => removePlanItem(i)}><FaTrash /></button>}
                </div>
                <div className="wp-task-body">
                  <div className="wp-frow">
                    <div className="wp-field">
                      <label className="wp-flbl"><FaTags /> Category</label>
                      <select value={item.category} onChange={(e) => updatePlanItem(i, 'category', e.target.value)} className="wp-input wp-select">
                        <option value="">Select category...</option>
                        {Object.entries(TASK_CATEGORIES).map(([k, c]) => <option key={k} value={k}>{c.label}</option>)}
                      </select>
                    </div>
                    <div className="wp-field wp-field-grow">
                      <label className="wp-flbl">What will you work on?</label>
                      <input type="text" value={item.description} onChange={(e) => updatePlanItem(i, 'description', e.target.value)} placeholder="Describe your planned task..." className="wp-input" />
                    </div>
                  </div>
                  {item.category && TASK_CATEGORIES[item.category]?.metrics.length > 0 && (
                    <div className="wp-metrics-row">
                      {TASK_CATEGORIES[item.category].metrics.map((mk) => (
                        <div key={mk.key} className="wp-field">
                          <label className="wp-flbl">{mk.label} (target)</label>
                          <input type="number" min="0" value={item.metrics?.[mk.key] || ''} onChange={(e) => updatePlanMetric(i, mk.key, e.target.value)} placeholder={mk.placeholder} className="wp-input wp-input-num" />
                        </div>
                      ))}
                    </div>
                  )}
                  {item.category === 'other' && (
                    <div className="wp-custom-metrics">
                      {(item.customMetrics || [{ key: '', value: '' }]).map((m, j) => (
                        <div key={j} className="wp-cm-row">
                          <input type="text" value={m.key} onChange={(e) => updatePlanCustomMetric(i, j, 'key', e.target.value)} placeholder="Metric name" className="wp-input" />
                          <input type="number" min="0" value={m.value} onChange={(e) => updatePlanCustomMetric(i, j, 'value', e.target.value)} placeholder="Target value" className="wp-input wp-input-sm" />
                          {(item.customMetrics || []).length > 1 && <button type="button" className="wp-icon-btn danger sm" onClick={() => removePlanCustomMetric(i, j)}><FaTrash /></button>}
                        </div>
                      ))}
                      <button type="button" className="wp-ghost-btn sm" onClick={() => addPlanCustomMetric(i)}><FaPlus /> Add Metric</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <button type="button" className="wp-ghost-btn" onClick={addPlanItem}><FaPlus /> Add Plan Item</button>

          <div className="wp-field" style={{ padding: '0 16px 16px' }}>
            <label className="wp-flbl"><FaExclamationTriangle style={{ color: 'var(--color-danger)' }} /> Blockers</label>
            <textarea value={blockers} onChange={(e) => setBlockers(e.target.value)} placeholder="Any blockers or challenges you need help with?" className="wp-textarea" rows={3} />
          </div>
        </div>

        {/* Submit */}
        <div className="wp-form-footer">
          <button type="submit" className="wp-submit-btn" disabled={submitting}>
            {submitting ? <><FaSpinner className="wp-spin" /> Saving...</>
              : existingReport ? <><FaRedo /> Update Report</>
              : <><FaCheckCircle /> Submit Report</>}
          </button>
          {existingReport && <p className="wp-form-hint">Updates are saved immediately and visible on the leaderboard.</p>}
        </div>
      </form>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// TAB 3 – LEADERBOARD
// ═════════════════════════════════════════════════════════════════════════════
const LeaderboardTab = () => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [weekStart, setWeekStart]     = useState('');
  const [loading, setLoading]         = useState(true);
  const { userData }                  = useAuth();

  useEffect(() => {
    (async () => {
      try {
        const data = await getLeaderboard();
        setLeaderboard(data.leaderboard || []);
        setWeekStart(data.weekStart);
      } catch (err) { console.error(err); toast.error('Failed to load leaderboard'); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <LoadingSpinner />;
  if (!leaderboard.length) return (
    <div className="wp-empty-panel">
      <div className="wp-empty-ico"><FaTrophy /></div>
      <div className="wp-empty-body"><strong>No submissions yet</strong><p>Be the first to submit a weekly report this week!</p></div>
    </div>
  );

  const medal = (r) => r === 1 ? '🥇' : r === 2 ? '🥈' : r === 3 ? '🥉' : null;
  const posClass = (r) => r === 1 ? 'gold' : r === 2 ? 'silver' : r === 3 ? 'bronze' : '';

  return (
    <div className="wp-lb">
      <div className="wp-lb-header">
        <h2 className="wp-lb-title"><FaTrophy style={{ color: '#d97706' }} /> Leaderboard</h2>
        <p className="wp-lb-sub">Week of {formatWeek(weekStart)}</p>
      </div>

      {/* Visual Podium */}
      {leaderboard.length >= 2 && (
        <div className="wp-podium">
          {[leaderboard[1], leaderboard[0], leaderboard[2]].filter(Boolean).map((entry, idx) => {
            const pos = ['silver', 'gold', 'bronze'][idx];
            return (
              <div key={entry.employee_id} className={`wp-podium-col ${pos}`}>
                <div className="wp-podium-top">
                  {pos === 'gold' && <span className="wp-podium-crown">👑</span>}
                  <div className={`wp-podium-av ${pos}`}>{(entry.employee_name || '?')[0].toUpperCase()}</div>
                  <div className="wp-podium-info">
                    <span className="wp-podium-name">{entry.employee_name}</span>
                    <span className={`wp-role-tag ${entry.employee_role}`}>{entry.employee_role}</span>
                  </div>
                </div>
                <div className={`wp-podium-block ${pos}`}>
                  <span className="wp-podium-rank">#{entry.rank}</span>
                  <span className="wp-podium-pts">{entry.performance_score}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Full Table */}
      <div className="wp-lb-table-wrap">
        <table className="wp-lb-table">
          <thead>
            <tr><th>Rank</th><th>Employee</th><th>Role</th><th>Completion</th><th>Score</th></tr>
          </thead>
          <tbody>
            {leaderboard.map((entry) => (
              <tr key={entry.employee_id} className={`${posClass(entry.rank) ? `lb-top-${entry.rank}` : ''} ${entry.employee_id === userData?.uid ? 'lb-me' : ''}`}>
                <td>{medal(entry.rank) ? <span className="wp-medal">{medal(entry.rank)}</span> : <span className="wp-rank-txt">#{entry.rank}</span>}</td>
                <td>
                  <div className="wp-lb-name">
                    <div className="wp-lb-av">{(entry.employee_name || '?')[0].toUpperCase()}</div>
                    <span>{entry.employee_name}</span>
                    {entry.employee_id === userData?.uid && <span className="wp-you-tag">You</span>}
                  </div>
                </td>
                <td><span className={`wp-role-tag ${entry.employee_role}`}>{entry.employee_role}</span></td>
                <td>
                  <div className="wp-lb-comp">
                    <span>{entry.completion_score}%</span>
                    <div className="wp-lb-bar-bg"><div className="wp-lb-bar-fill" style={{ width: `${entry.completion_score}%`, background: entry.completion_score >= 80 ? '#10b981' : entry.completion_score >= 50 ? '#f59e0b' : '#ef4444' }} /></div>
                  </div>
                </td>
                <td><span className="wp-score-val">{entry.performance_score}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// TAB 4 – ALL REPORTS
// ═════════════════════════════════════════════════════════════════════════════
const AllReportsTab = () => {
  const { userData }                  = useAuth();
  const [reports, setReports]         = useState([]);
  const [weeks, setWeeks]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [expandedId, setExpandedId]   = useState(null);
  const [filterWeek, setFilterWeek]   = useState('');
  const [filterRole, setFilterRole]   = useState('');
  const [filterName, setFilterName]   = useState('');

  const callerRole = userData?.role || 'employee';
  const roleOpts = callerRole === 'superadmin'
    ? [{ value: 'employee', label: 'Employee' }, { value: 'admin', label: 'Admin' }, { value: 'superadmin', label: 'Super Admin' }]
    : callerRole === 'admin' ? [{ value: 'employee', label: 'Employee' }, { value: 'admin', label: 'Admin' }]
    : [{ value: 'employee', label: 'Employee' }];

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const f = {};
      if (filterWeek) f.week = filterWeek;
      if (filterRole) f.role = filterRole;
      if (filterName.trim()) f.employeeName = filterName.trim();
      const data = await getAllReports(f);
      setReports(data.reports || []);
    } catch (err) { console.error(err); toast.error('Failed to load reports'); }
    finally { setLoading(false); }
  }, [filterWeek, filterRole, filterName]);

  useEffect(() => {
    (async () => { try { const d = await getReportWeeks(); setWeeks(d.weeks || []); } catch (e) { console.error(e); } })();
  }, []);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const hasFilters = filterWeek || filterRole || filterName;

  return (
    <div className="wp-ar">
      <div className="wp-ar-header">
        <h2 className="wp-ar-title">All Performance Reports</h2>
        <div className="wp-ar-filters">
          <div className="wp-filter-search">
            <FaFilter className="wp-filter-ico" />
            <input type="text" value={filterName} onChange={(e) => setFilterName(e.target.value)} placeholder="Search by name..." className="wp-filter-input" />
          </div>
          <select value={filterWeek} onChange={(e) => setFilterWeek(e.target.value)} className="wp-filter-select">
            <option value="">All Weeks</option>
            {weeks.map((w) => <option key={w} value={w}>{formatWeek(w)}</option>)}
          </select>
          <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)} className="wp-filter-select">
            <option value="">All Roles</option>
            {roleOpts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {hasFilters && <button className="wp-filter-clear" onClick={() => { setFilterWeek(''); setFilterRole(''); setFilterName(''); }}>✕ Clear</button>}
        </div>
      </div>

      {loading ? <LoadingSpinner /> : !reports.length ? (
        <div className="wp-empty-panel">
          <div className="wp-empty-ico"><FaFileAlt /></div>
          <div className="wp-empty-body"><strong>No reports found</strong><p>{hasFilters ? 'Try adjusting your filters.' : 'No reports have been submitted yet.'}</p></div>
        </div>
      ) : (
        <div className="wp-ar-list">
          {reports.map((report) => (
            <div key={report.id} className={`wp-ar-card${expandedId === report.id ? ' expanded' : ''}`}>
              <div className="wp-ar-card-head" onClick={() => setExpandedId(expandedId === report.id ? null : report.id)}>
                <div className="wp-ar-card-left">
                  <div className="wp-ar-av">{(report.employee_name || '?')[0].toUpperCase()}</div>
                  <div className="wp-ar-info">
                    <div className="wp-ar-name-row">
                      <strong>{report.employee_name}</strong>
                      <span className={`wp-role-tag ${report.employee_role}`}>{report.employee_role}</span>
                    </div>
                    <span className="wp-ar-week"><FaCalendarAlt /> {formatWeek(report.week_start_date)}</span>
                  </div>
                </div>
                <div className="wp-ar-card-right">
                  <div className="wp-score-chip primary"><span className="wp-sc-v">{report.performance_score}</span><span className="wp-sc-l">Score</span></div>
                  <div className={`wp-score-chip ${report.completion_score >= 80 ? 'success' : report.completion_score >= 50 ? 'warning' : 'danger'}`}><span className="wp-sc-v">{report.completion_score}%</span><span className="wp-sc-l">Done</span></div>
                  <span className="wp-ar-expand">{expandedId === report.id ? <FaChevronUp /> : <FaChevronDown />}</span>
                </div>
              </div>

              {expandedId === report.id && (
                <div className="wp-ar-detail">
                  {report.tasks?.length > 0 ? (
                    <div className="wp-dsec">
                      <h4 className="wp-dsec-title"><FaListOl /> Tasks</h4>
                      {report.tasks.map((task, i) => (
                        <div key={i} className="wp-dtask">
                          <div className="wp-dtask-row">
                            <span className="wp-dtn">{i + 1}.</span>
                            <strong>{task.description}</strong>
                            {task.category && <span className="wp-tag cat sm">{TASK_CATEGORIES[task.category]?.label || task.category}</span>}
                            {task.status && <span className={`wp-tag status-${task.status} sm`}>{task.status === 'done' ? '✓ Done' : task.status === 'partially-done' ? '◐ Partial' : '✗ Not Done'}</span>}
                          </div>
                          {task.metrics && Object.keys(task.metrics).length > 0 && (
                            <div className="wp-dtask-metrics">
                              {Object.entries(task.metrics).map(([k, v]) => (
                                <span key={k} className="wp-mchip"><span className="wp-mck">{humanizeMetricKey(k)}</span><span className="wp-mcv">{v}</span></span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : report.last_week_commitments?.length > 0 ? (
                    <div className="wp-dsec"><h4 className="wp-dsec-title">Commitments</h4><ul className="wp-dlist">{report.last_week_commitments.map((c, i) => <li key={i}>{c}</li>)}</ul></div>
                  ) : null}

                  {!report.tasks?.length && report.metrics && Object.keys(report.metrics).length > 0 && (
                    <div className="wp-dsec">
                      <h4 className="wp-dsec-title">Metrics</h4>
                      <div className="wp-dtask-metrics">{Object.entries(report.metrics).map(([k, v]) => <span key={k} className="wp-mchip"><span className="wp-mck">{k}</span><span className="wp-mcv">{v}</span></span>)}</div>
                    </div>
                  )}

                  <div className="wp-d2col">
                    {report.wins && <div className="wp-dcard wins"><h4><FaStar style={{ color: '#d97706' }} /> Wins</h4><p>{report.wins}</p></div>}
                    {report.failures && <div className="wp-dcard learnings"><h4><FaLightbulb style={{ color: '#d97706' }} /> Learnings</h4><p>{report.failures}</p></div>}
                  </div>

                  {report.next_week_plan?.length > 0 && (
                    <div className="wp-dsec">
                      <h4 className="wp-dsec-title"><FaCalendarAlt style={{ color: '#6366f1' }} /> Next Week Plan</h4>
                      {report.next_week_plan.map((p, i) => (
                        <div key={i} className="wp-dtask">
                          <div className="wp-dtask-row">
                            <span className="wp-dtn">{i + 1}.</span>
                            <strong>{typeof p === 'string' ? p : p.description}</strong>
                            {typeof p !== 'string' && p.category && <span className="wp-tag cat sm">{TASK_CATEGORIES[p.category]?.label || p.category}</span>}
                          </div>
                          {typeof p !== 'string' && p.metrics && Object.keys(p.metrics).length > 0 && (
                            <div className="wp-dtask-metrics">{Object.entries(p.metrics).map(([k, v]) => <span key={k} className="wp-mchip"><span className="wp-mck">{humanizeMetricKey(k)}</span><span className="wp-mcv">{v}</span></span>)}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {report.blockers && (
                    <div className="wp-dsec"><h4 className="wp-dsec-title"><FaExclamationTriangle style={{ color: 'var(--color-danger)' }} /> Blockers</h4><p className="wp-dtext">{report.blockers}</p></div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Loading Spinner ───────────────────────────────────────────────────────────
const LoadingSpinner = () => (
  <div className="wp-loading">
    <div className="wp-loading-dots">
      <div className="wp-loading-dot" />
      <div className="wp-loading-dot" />
      <div className="wp-loading-dot" />
    </div>
    <span>Loading...</span>
  </div>
);

export default WeeklyPerformance;
